import type { SiteMode } from '../routing/siteMode';

/** Dev-controlled link target — editors cannot override hrefs. */
export type NavTarget =
  | { kind: 'page'; slug: string }
  | { kind: 'anchor'; anchor: string }
  | { kind: 'href'; href: string };

export type NavMenuLayout = 'default' | 'dropdown' | 'mega';

export interface NavTreeNodeDef {
  id: string;
  target: NavTarget;
  /** Default label when the editor leaves the override empty. */
  labels: { de: string; en: string; vi: string };
  children?: NavTreeNodeDef[];
  layout?: NavMenuLayout;
}

export interface NavTreeDefinition {
  nodes: NavTreeNodeDef[];
}

function loc3(de: string, en: string, vi: string): { de: string; en: string; vi: string } {
  return { de, en, vi };
}

/**
 * Footer `id="contact"` ships on every page (global chrome).
 * Same-page `#contact` scrolls to the footer wherever you are — do not homepage-root
 * (`/#contact`), or preview/studio jumps to Home (dish highlight looks like Menu).
 * Minimal sites keep this; standard sites link the dedicated `/contact` page.
 */
const CONTACT_FOOTER_TARGET: NavTarget = { kind: 'href', href: '#contact' };
const CONTACT_PAGE_TARGET: NavTarget = { kind: 'page', slug: 'contact' };

/** Minimal / single-page — scroll to footer contact. */
const CONTACT_NAV_NODE: NavTreeNodeDef = {
  id: 'contact',
  target: CONTACT_FOOTER_TARGET,
  labels: loc3('Kontakt', 'Contact', 'Liên hệ'),
};

/** Standard multipage — Kontakt route (form + visit details). */
const CONTACT_PAGE_NAV_NODE: NavTreeNodeDef = {
  id: 'contact',
  target: CONTACT_PAGE_TARGET,
  labels: loc3('Kontakt', 'Contact', 'Liên hệ'),
};

const MINIMAL_ANCHOR_NAV: NavTreeNodeDef[] = [
  {
    id: 'menu',
    target: { kind: 'anchor', anchor: '#menu' },
    labels: loc3('Speisekarte', 'Menu', 'Thực đơn'),
  },
  {
    id: 'about',
    target: { kind: 'anchor', anchor: '#about' },
    labels: loc3('Über uns', 'About', 'Giới thiệu'),
  },
  CONTACT_NAV_NODE,
];

/** Cafe minimum — hours + map always ship; no Speisekarte section. */
const CAFE_MINIMAL_NAV: NavTreeNodeDef[] = [
  {
    id: 'hours',
    target: { kind: 'anchor', anchor: '#hours' },
    labels: loc3('Öffnungszeiten', 'Hours', 'Giờ mở cửa'),
  },
  {
    id: 'map',
    target: { kind: 'anchor', anchor: '#map' },
    labels: loc3('Standort', 'Location', 'Địa điểm'),
  },
  CONTACT_NAV_NODE,
];

/**
 * Cafe standard — Menü · About · Kontakt.
 * Hours and the map stay on Home; the button is Öffnungszeiten.
 */
const CAFE_STANDARD_NAV: NavTreeNodeDef[] = [
  {
    id: 'bakery',
    target: { kind: 'anchor', anchor: '#bakery' },
    labels: loc3('Menü', 'Menu', 'Thực đơn'),
  },
  {
    id: 'about-page',
    target: { kind: 'page', slug: 'about-us' },
    labels: loc3('Über uns', 'About', 'Giới thiệu'),
  },
  CONTACT_PAGE_NAV_NODE,
];

/** Bar minimum — drinks list always ships. */
const BAR_MINIMAL_NAV: NavTreeNodeDef[] = [
  {
    id: 'drinks',
    target: { kind: 'anchor', anchor: '#drinks' },
    labels: loc3('Getränke', 'Drinks', 'Đồ uống'),
  },
  {
    id: 'about',
    target: { kind: 'anchor', anchor: '#about' },
    labels: loc3('Über uns', 'About', 'Giới thiệu'),
  },
  CONTACT_NAV_NODE,
];

/**
 * Bar standard — Getränke · Events · Kontakt.
 * Signature stays on Home; the button is Event buchen / VIP.
 */
const BAR_STANDARD_NAV: NavTreeNodeDef[] = [
  {
    id: 'drinks',
    target: { kind: 'anchor', anchor: '#drinks' },
    labels: loc3('Getränke', 'Drinks', 'Đồ uống'),
  },
  {
    id: 'about-page',
    target: { kind: 'page', slug: 'about-us' },
    labels: loc3('Events', 'Events', 'Sự kiện'),
  },
  CONTACT_PAGE_NAV_NODE,
];

/** Food truck minimum — strip menu + static location always ship. */
const FOOD_TRUCK_MINIMAL_NAV: NavTreeNodeDef[] = [
  {
    id: 'menu',
    target: { kind: 'anchor', anchor: '#menu' },
    labels: loc3('Menü', 'Menu', 'Thực đơn'),
  },
  {
    id: 'location',
    target: { kind: 'anchor', anchor: '#location' },
    labels: loc3('Standort', 'Find us', 'Vị trí'),
  },
  CONTACT_NAV_NODE,
];

/**
 * Food truck standard — Menü · Catering · Kontakt.
 * Today's stop and the weekly route stay on Home; the button is Standort.
 */
const FOOD_TRUCK_STANDARD_NAV: NavTreeNodeDef[] = [
  {
    id: 'menu',
    target: { kind: 'anchor', anchor: '#menu' },
    labels: loc3('Menü', 'Menu', 'Thực đơn'),
  },
  {
    id: 'about-page',
    target: { kind: 'page', slug: 'about-us' },
    labels: loc3('Catering', 'Catering', 'Catering'),
  },
  CONTACT_PAGE_NAV_NODE,
];

const STANDARD_FLAT_NAV: NavTreeNodeDef[] = [
  {
    id: 'home',
    target: { kind: 'page', slug: 'index' },
    labels: loc3('Start', 'Home', 'Trang chủ'),
  },
  {
    id: 'about',
    target: { kind: 'page', slug: 'about-us' },
    labels: loc3('Über uns', 'About', 'Giới thiệu'),
  },
  CONTACT_PAGE_NAV_NODE,
];

/** Restaurant standard — flat top-level links matching the page tree (no mega Discover). */
const RESTAURANT_STANDARD_NAV: NavTreeNodeDef[] = [
  {
    id: 'home',
    target: { kind: 'page', slug: 'index' },
    labels: loc3('Start', 'Home', 'Trang chủ'),
  },
  {
    id: 'menu',
    target: { kind: 'page', slug: 'menu' },
    labels: loc3('Speisekarte', 'Menu', 'Thực đơn'),
  },
  {
    id: 'about-page',
    target: { kind: 'page', slug: 'about-us' },
    labels: loc3('Über uns', 'About us', 'Về chúng tôi'),
  },
  CONTACT_PAGE_NAV_NODE,
];

/** Sushi bar minimal — single-page anchors. */
function sushiBarMinimalNav(hasTastingTimeline?: boolean): NavTreeNodeDef[] {
  const nodes: NavTreeNodeDef[] = [
    {
      id: 'menu',
      target: { kind: 'anchor', anchor: '#menu' },
      labels: loc3('Speisekarte', 'Menu', 'Thực đơn'),
    },
  ];
  // Omakase only when the tasting timeline ships (Plus) — never a dead/mislabelled link.
  if (hasTastingTimeline) {
    nodes.push({
      id: 'omakase',
      target: { kind: 'anchor', anchor: '#tasting' },
      labels: loc3('Omakase', 'Omakase', 'Omakase'),
    });
  }
  nodes.push(CONTACT_NAV_NODE);
  return nodes;
}

/** Sushi bar standard — Speisekarte + optional Omakase + About + contact. */
function sushiBarStandardNav(hasTastingTimeline?: boolean): NavTreeNodeDef[] {
  const nodes: NavTreeNodeDef[] = [
    {
      id: 'menu',
      target: { kind: 'page', slug: 'menu' },
      labels: loc3('Speisekarte', 'Menu', 'Thực đơn'),
    },
  ];
  if (hasTastingTimeline) {
    nodes.push({
      id: 'omakase',
      target: { kind: 'anchor', anchor: '#tasting' },
      labels: loc3('Omakase', 'Omakase', 'Omakase'),
    });
  }
  nodes.push(
    {
      id: 'about-page',
      target: { kind: 'page', slug: 'about-us' },
      labels: loc3('Über uns', 'About us', 'Về chúng tôi'),
    },
    CONTACT_PAGE_NAV_NODE,
  );
  return nodes;
}

/** Imbiss standard — Speisekarte page + contact; no About (family omits `/about-us`). */
const IMBISS_STANDARD_NAV: NavTreeNodeDef[] = [
  {
    id: 'home',
    target: { kind: 'page', slug: 'index' },
    labels: loc3('Start', 'Home', 'Trang chủ'),
  },
  {
    id: 'menu',
    target: { kind: 'page', slug: 'menu' },
    labels: loc3('Speisekarte', 'Menu', 'Thực đơn'),
  },
  CONTACT_PAGE_NAV_NODE,
];

/** Neighbourhood bistro minimal — single-page anchors. */
const BISTRO_MINIMAL_NAV: NavTreeNodeDef[] = [
  {
    id: 'menu',
    target: { kind: 'anchor', anchor: '#menu' },
    labels: loc3('Speisekarte', 'Menu', 'Thực đơn'),
  },
  {
    id: 'about',
    target: { kind: 'anchor', anchor: '#about' },
    labels: loc3('Über uns', 'About', 'Giới thiệu'),
  },
  CONTACT_NAV_NODE,
];

/** Neighbourhood bistro standard — Speisekarte + About (chef) + contact; reserve is CTA. */
const BISTRO_STANDARD_NAV: NavTreeNodeDef[] = [
  {
    id: 'menu',
    target: { kind: 'page', slug: 'menu' },
    labels: loc3('Speisekarte', 'Menu', 'Thực đơn'),
  },
  {
    id: 'about-page',
    target: { kind: 'page', slug: 'about-us' },
    labels: loc3('Über uns', 'About us', 'Về chúng tôi'),
  },
  CONTACT_PAGE_NAV_NODE,
];

const SUSHI_BAR_THEME_ID = 'hinoki-counter';
/** All restaurant Imbiss souls — Speisekarte · Kontakt · call (no About). */
const IMBISS_THEME_IDS = new Set(['bold-street', 'mustard-rail', 'tahini-window']);
const BISTRO_THEME_ID = 'bistro-brasserie';
const VIETNAM_THEME_IDS = new Set(['lotus-steam', 'tile-awning']);
const ASIAN_THEME_ID = 'lantern-court';

const NAV_TREE_BY_VERTICAL_SITE: Record<string, Partial<Record<SiteMode, NavTreeDefinition>>> = {
  restaurant: {
    minimal: { nodes: MINIMAL_ANCHOR_NAV },
    standard: { nodes: RESTAURANT_STANDARD_NAV },
  },
  cafe: {
    minimal: { nodes: CAFE_MINIMAL_NAV },
    standard: { nodes: CAFE_STANDARD_NAV },
  },
  bar: {
    minimal: { nodes: BAR_MINIMAL_NAV },
    standard: { nodes: BAR_STANDARD_NAV },
  },
  food_truck: {
    minimal: { nodes: FOOD_TRUCK_MINIMAL_NAV },
    standard: { nodes: FOOD_TRUCK_STANDARD_NAV },
  },
};

const DEFAULT_NAV_TREE: Record<SiteMode, NavTreeDefinition> = {
  minimal: { nodes: MINIMAL_ANCHOR_NAV },
  standard: { nodes: STANDARD_FLAT_NAV },
};

/** Dev-authored navigation topology for a vertical + site mode (+ optional theme). */
export function getNavTreeDefinition(input: {
  vertical?: string;
  siteMode: SiteMode;
  themeId?: string;
  /** When true, sushi Omakase points at `#tasting` (Plus timeline on Home). */
  hasTastingTimeline?: boolean;
}): NavTreeDefinition {
  const vertical = input.vertical?.trim() || 'restaurant';
  if (
    vertical === 'restaurant' &&
    input.themeId === SUSHI_BAR_THEME_ID &&
    input.siteMode === 'standard'
  ) {
    return { nodes: sushiBarStandardNav(input.hasTastingTimeline) };
  }
  if (
    vertical === 'restaurant' &&
    input.themeId === SUSHI_BAR_THEME_ID &&
    input.siteMode === 'minimal'
  ) {
    return { nodes: sushiBarMinimalNav(input.hasTastingTimeline) };
  }
  if (
    vertical === 'restaurant' &&
    input.themeId != null &&
    IMBISS_THEME_IDS.has(input.themeId) &&
    input.siteMode === 'standard'
  ) {
    return { nodes: IMBISS_STANDARD_NAV };
  }
  if (
    vertical === 'restaurant' &&
    input.themeId === BISTRO_THEME_ID &&
    input.siteMode === 'standard'
  ) {
    return { nodes: BISTRO_STANDARD_NAV };
  }
  if (
    vertical === 'restaurant' &&
    input.themeId === BISTRO_THEME_ID &&
    input.siteMode === 'minimal'
  ) {
    return { nodes: BISTRO_MINIMAL_NAV };
  }
  if (
    vertical === 'restaurant' &&
    input.themeId &&
    (VIETNAM_THEME_IDS.has(input.themeId) || input.themeId === ASIAN_THEME_ID) &&
    input.siteMode === 'standard'
  ) {
    return { nodes: BISTRO_STANDARD_NAV };
  }
  if (
    vertical === 'restaurant' &&
    input.themeId &&
    (VIETNAM_THEME_IDS.has(input.themeId) || input.themeId === ASIAN_THEME_ID) &&
    input.siteMode === 'minimal'
  ) {
    return { nodes: BISTRO_MINIMAL_NAV };
  }
  const entry = NAV_TREE_BY_VERTICAL_SITE[vertical]?.[input.siteMode];
  return entry ?? DEFAULT_NAV_TREE[input.siteMode];
}

/** Flat list of all node ids in a tree (for editor field discovery). */
export function collectNavTreeNodeIds(nodes: NavTreeNodeDef[]): string[] {
  const ids: string[] = [];
  const walk = (list: NavTreeNodeDef[]) => {
    for (const node of list) {
      ids.push(node.id);
      if (node.children?.length) {
        walk(node.children);
      }
    }
  };
  walk(nodes);
  return ids;
}
